import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bot, KeyRound, Github, Check, Copy, FolderGit2, RefreshCw, Lock, Globe,
  Play, Loader2, CheckCircle2, XCircle, Clock, Zap, Settings2,
  ChevronDown, ChevronUp, RotateCcw, Send, History, SlidersHorizontal,
  AlertCircle, GitBranch,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getAgentSettings, updateAgentSettings, getAgentRepos,
  runAgentJob, getAgentJobStatus, getAgentJobs,
} from '../api';
import GitHubAppCard from '../components/agents/GitHubAppCard';
import { useOrg } from '../contexts/OrgContext';

// ── tiny helpers ─────────────────────────────────────────────────────────────

function relativeTime(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatLocal(iso) {
  // Returns time as "HH:MM" for today, "DD MMM HH:MM" for older — in the
  // viewer's own local timezone (browser default), not a hardcoded offset.
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (sameDay) return `${hh}:${mm}`;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${hh}:${mm}`;
}

function duration(start, end) {
  if (!start || !end) return null;
  const s = Math.round((new Date(end) - new Date(start)) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

const STATUS_META = {
  succeeded: { color: 'text-green-400',  bg: 'bg-green-400/10', border: 'border-green-400/20', icon: CheckCircle2, label: 'Done'    },
  failed:    { color: 'text-red-400',    bg: 'bg-red-400/10',   border: 'border-red-400/20',   icon: XCircle,      label: 'Failed'  },
  running:   { color: 'text-orange',     bg: 'bg-orange/10',    border: 'border-orange/20',     icon: Loader2,      label: 'Running' },
  queued:    { color: 'text-fg-muted',   bg: 'bg-fg/5',         border: 'border-panel-border',  icon: Clock,        label: 'Queued'  },
  timeout:   { color: 'text-yellow-400', bg: 'bg-yellow-400/10',border: 'border-yellow-400/20', icon: AlertCircle,  label: 'Timeout' },
};

function StatusBadge({ status, small }) {
  const m = STATUS_META[status] || STATUS_META.queued;
  const Icon = m.icon;
  const spin = status === 'running';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
      ${m.color} ${m.bg} border ${m.border} ${small ? 'text-[10px]' : ''}`}>
      <Icon className={`w-3 h-3 ${spin ? 'animate-spin' : ''}`} />
      {m.label}
    </span>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────────────────

function Tabs({ active, onChange }) {
  return (
    <div className="flex gap-1 p-1 bg-fg/5 rounded-xl mb-6 w-fit">
      {[
        { id: 'jobs',     icon: Zap,           label: 'Agent'    },
        { id: 'settings', icon: SlidersHorizontal, label: 'Settings' },
      ].map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150
            ${active === id
              ? 'bg-panel text-fg shadow-sm border border-panel-border'
              : 'text-fg-muted hover:text-fg'}`}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Job card ──────────────────────────────────────────────────────────────────

function JobCard({ job, isActive, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const isLive = job.status === 'running' || job.status === 'queued';

  return (
    <div
      onClick={() => onSelect(job)}
      className={`rounded-xl border transition-all duration-150 cursor-pointer group
        ${isActive
          ? 'border-orange/40 bg-orange/5'
          : 'border-panel-border bg-panel hover:border-fg/20 hover:bg-fg/5'}`}
    >
      {/* Header row */}
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-fg font-medium leading-snug line-clamp-2 mb-1.5">
            {job.prompt}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={job.status} small />
            <span className="text-[11px] text-fg-muted" title={formatLocal(job.submitted_at)}>
              {relativeTime(job.submitted_at)}
            </span>
            {job.finished_at && (
              <span className="text-[11px] text-fg-muted flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" />
                {duration(job.submitted_at, job.finished_at)}
              </span>
            )}
            {isLive && (
              <span className="text-[11px] text-orange font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-orange animate-pulse inline-block" />
                live
              </span>
            )}
          </div>
        </div>
        {/* Expand toggle */}
        {(job.result_text || job.error) && (
          <button
            onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            className="text-fg-muted hover:text-fg mt-0.5 shrink-0"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Expanded result */}
      {expanded && (
        <div className="border-t border-panel-border mx-4 mb-4 pt-3">
          {job.result_text && (
            <div className="agent-markdown text-xs text-fg leading-relaxed line-clamp-6 overflow-hidden">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {job.result_text}
              </ReactMarkdown>
            </div>
          )}
          {job.error && (
            <p className="text-xs text-red-400 font-mono">{job.error}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Active job panel ──────────────────────────────────────────────────────────

function ActiveJobPanel({ job, onClose }) {
  if (!job) return (
    <div className="flex flex-col items-center justify-center h-full text-center py-16 px-8">
      <div className="w-14 h-14 rounded-2xl bg-orange/10 border border-orange/20 flex items-center justify-center mb-4">
        <Bot className="w-7 h-7 text-orange" />
      </div>
      <p className="text-fg font-semibold mb-1">Select a job to see details</p>
      <p className="text-fg-muted text-sm">Click any job on the left to view its full result here.</p>
    </div>
  );

  const isLive = job.status === 'running' || job.status === 'queued';

  return (
    <div className="flex flex-col h-full">
      {/* Job header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <StatusBadge status={job.status} />
          <p className="text-fg font-semibold mt-2 leading-snug">{job.prompt}</p>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-fg-muted flex-wrap">
            <span title={formatLocal(job.submitted_at)}>{relativeTime(job.submitted_at)}</span>
            <span className="text-fg/30">·</span>
            <span>{formatLocal(job.submitted_at)}</span>
            {job.finished_at && <span>{duration(job.submitted_at, job.finished_at)}</span>}
            <span className="font-mono truncate max-w-[140px]">{job.model?.split('/').slice(-1)[0]}</span>
            <span className="font-mono text-fg/30">{job.job_id?.slice(0, 8)}…</span>
          </div>
        </div>
      </div>

      {/* Result area */}
      <div className="flex-1 overflow-y-auto">
        {isLive && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-orange/5 border border-orange/20 mb-4">
            <Loader2 className="w-5 h-5 text-orange animate-spin shrink-0" />
            <div>
              <p className="text-sm font-semibold text-fg">Agent is working…</p>
              <p className="text-xs text-fg-muted">Running in a secure sandbox. This may take a few minutes.</p>
            </div>
          </div>
        )}

        {job.result_text && (
          <div className="rounded-xl border border-panel-border bg-surface p-4">
            <p className="text-xs text-fg-muted mb-3 font-semibold uppercase tracking-wide">Result</p>
            <div className="agent-markdown text-sm text-fg leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {job.result_text}
              </ReactMarkdown>
            </div>
          </div>
        )}

        {job.error && (
          <div className="rounded-xl border border-red-400/20 bg-red-400/5 p-4">
            <p className="text-xs text-red-400 mb-2 font-semibold uppercase tracking-wide flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Error
            </p>
            <p className="text-sm text-red-400 font-mono">{job.error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── GitHub App card ───────────────────────────────────────────────────────────
// The org's GitHub App connection. Managers connect it (a GitHub sign-in links every
// installation they can see; /github/setup finishes it) and tick which repos agents may
// use -- none until picked. Members see the ticked repos, read-only.

// ── Main component ────────────────────────────────────────────────────────────

export default function AgentSettings() {
  const { active: activeOrg } = useOrg();
  const location = useLocation();   // back from /github/setup: open Settings, say what was linked
  const [tab, setTab]         = useState(location.state?.tab === 'settings' ? 'settings' : 'jobs');
  const [loading, setLoading] = useState(true);

  // ── settings state ────────────────────────────────────────────────────────
  const [s, setS]               = useState({ api_key: '', agent_model: '', github_token_set: false, github_token_last4: '', allowed_repos: [], models: [] });
  const [token, setToken]       = useState('');
  const [model, setModel]       = useState('');
  const [copied, setCopied]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState('');
  const [repos, setRepos]       = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoErr, setRepoErr]   = useState('');

  // ── jobs state ────────────────────────────────────────────────────────────
  const [jobs, setJobs]             = useState([]);
  const [jobsTotal, setJobsTotal]   = useState(0);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [activeJob, setActiveJob]   = useState(null);

  // ── composer state ────────────────────────────────────────────────────────
  const [prompt, setPrompt]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [liveJobId, setLiveJobId]   = useState(null);

  const pollRef     = useRef(null);
  const tickRef     = useRef(null);   // drives timestamp re-renders
  const textareaRef = useRef(null);
  const [, setTick] = useState(0);    // incremented every 30s to force re-render

  // ── load settings ─────────────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    try {
      const r = await getAgentSettings();
      setS(r.data);
      setModel(r.data.agent_model || r.data.models?.[0] || '');
      setSelected(r.data.allowed_repos || []);
    } catch { /* ignore */ }
  }, []);

  // ── load job history ──────────────────────────────────────────────────────
  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const r = await getAgentJobs(30, 0);
      setJobs(r.data.jobs || []);
      setJobsTotal(r.data.total || 0);
    } catch { /* ignore */ }
    finally { setLoadingJobs(false); }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadSettings(), loadJobs()]);
      setLoading(false);
    })();
  }, [loadSettings, loadJobs]);

  // ── tick every 30s — re-renders timestamps + silently refreshes job list ──
  useEffect(() => {
    tickRef.current = setInterval(() => {
      setTick(t => t + 1);   // forces relativeTime() to recompute
      loadJobs();             // silently re-fetches list from server
    }, 30000);
    return () => clearInterval(tickRef.current);
  }, [loadJobs]);

  // ── live polling for active job ───────────────────────────────────────────
  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => {
    if (!liveJobId) return;
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await getAgentJobStatus(liveJobId);
        const updated = r.data;
        // update in list
        setJobs(prev => prev.map(j => j.job_id === liveJobId ? { ...j, ...updated } : j));
        // update active panel if this is the selected one
        setActiveJob(prev => prev?.job_id === liveJobId ? { ...prev, ...updated } : prev);
        if (updated.status === 'succeeded' || updated.status === 'failed') {
          stopPolling();
          setLiveJobId(null);
          setSubmitting(false);
          loadJobs();   // re-fetch full list so finished_at and result are fresh
        }
      } catch { /* keep polling */ }
    }, 3000);
    return stopPolling;
  }, [liveJobId]);

  useEffect(() => () => stopPolling(), []);

  // ── submit job ────────────────────────────────────────────────────────────
  const submitJob = async () => {
    if (!prompt.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await runAgentJob(prompt.trim(), model || undefined);
      const jid = res.data.job_id;
      const newJob = {
        job_id: jid,
        status: 'queued',
        prompt: prompt.trim(),
        model: model,
        submitted_at: new Date().toISOString(),
        finished_at: null,
        result_text: null,
        error: null,
      };
      setJobs(prev => [newJob, ...prev]);
      setActiveJob(newJob);
      setLiveJobId(jid);
      setPrompt('');
    } catch (e) {
      setSubmitting(false);
      const errJob = {
        job_id: 'err-' + Date.now(),
        status: 'failed',
        prompt: prompt.trim(),
        error: e.response?.data?.detail || 'Failed to submit',
        submitted_at: new Date().toISOString(),
      };
      setJobs(prev => [errJob, ...prev]);
      setActiveJob(errJob);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submitJob();
  };

  // ── settings helpers ──────────────────────────────────────────────────────
  const loadRepos = async () => {
    setLoadingRepos(true); setRepoErr('');
    try {
      if (token.trim()) { await updateAgentSettings({ github_token: token.trim() }); setToken(''); await loadSettings(); }
      const r = await getAgentRepos();
      setRepos(r.data.repos || []);
    } catch (e) { setRepoErr(e.response?.data?.detail || 'Could not fetch repos'); }
    finally { setLoadingRepos(false); }
  };

  const toggleRepo = (name) =>
    setSelected(cur => cur.includes(name) ? cur.filter(x => x !== name) : [...cur, name]);

  const save = async () => {
    setSaving(true); setSaveMsg('');
    try {
      const payload = { agent_model: model, allowed_repos: selected };
      if (token.trim()) payload.github_token = token.trim();
      await updateAgentSettings(payload);
      setToken(''); setSaveMsg('Saved ✓');
      await loadSettings();
    } catch (e) { setSaveMsg(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const clearToken = async () => {
    setSaving(true); setSaveMsg('');
    try { await updateAgentSettings({ clear_github_token: true }); setRepos([]); setSaveMsg('Token removed'); await loadSettings(); }
    catch { setSaveMsg('Failed'); }
    finally { setSaving(false); }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(s.api_key || '');
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  // ── readiness check ───────────────────────────────────────────────────────
  // The sandbox gets its repo from an on-prem bind-mount (the execution engine
  // is a separate app), so the UI no longer requires a GitHub token or repo
  // selection to submit a job — only a model.
  const isReady = !!model;

  if (loading) return (
    <div className="flex items-center justify-center py-24 text-fg-muted">
      <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">

      {/* Page header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg flex items-center gap-2">
            <Bot className="w-6 h-6 text-orange" />LatentStack Agent
          </h1>
          <p className="text-fg-muted text-sm mt-1">
            Run autonomous coding tasks in a secure sandbox using your repos and credentials.
          </p>
        </div>
        {/* Readiness indicator */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border
          ${isReady
            ? 'text-green-400 bg-green-400/10 border-green-400/20'
            : 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20'}`}>
          <span className={`w-2 h-2 rounded-full ${isReady ? 'bg-green-400' : 'bg-yellow-400'}`} />
          {isReady ? 'Ready' : 'Setup required'}
        </div>
      </div>

      <Tabs active={tab} onChange={setTab} />

      {/* ═══════════════════════════════════════════════════════════════════
          TAB 1 — AGENT (job composer + history)
      ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'jobs' && (
        <div className="flex gap-5" style={{ minHeight: '70vh' }}>

          {/* LEFT — composer + history list */}
          <div className="w-[380px] shrink-0 flex flex-col gap-3">

            {/* Setup nudge */}
            {!isReady && (
              <button
                onClick={() => setTab('settings')}
                className="flex items-center gap-2 p-3 rounded-xl border border-yellow-400/30 bg-yellow-400/5 text-yellow-400 text-xs hover:bg-yellow-400/10 transition-colors w-full text-left"
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>Choose a model in Settings.</span>
                <Settings2 className="w-3.5 h-3.5 ml-auto shrink-0" />
              </button>
            )}

            {/* Composer card */}
            <div className={`rounded-2xl border bg-panel p-4 transition-all
              ${submitting ? 'border-orange/40' : 'border-panel-border'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Send className="w-4 h-4 text-orange" />
                <span className="text-sm font-semibold text-fg">New Job</span>
                {model && (
                  <span className="ml-auto text-[11px] text-fg-muted bg-fg/5 px-2 py-0.5 rounded-full truncate max-w-[120px]">
                    {model.split('/').slice(-1)[0]}
                  </span>
                )}
              </div>

              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isReady
                  ? "Describe what you want the agent to do…\ne.g. Write a VAPT report and raise a PR"
                  : "Complete setup in Settings first…"}
                disabled={!isReady || submitting}
                rows={4}
                className="w-full text-sm bg-surface border border-field-border rounded-xl px-3 py-2.5 text-fg
                  focus:border-orange outline-none resize-none placeholder:text-fg-muted/60
                  disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              />

              <div className="flex items-center justify-between mt-3">
                <span className="text-[11px] text-fg-muted">⌘↵ to submit</span>
                <button
                  onClick={submitJob}
                  disabled={!prompt.trim() || !isReady || submitting}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange hover:bg-orange-hover
                    text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Running…</>
                    : <><Play className="w-3.5 h-3.5" />Run</>}
                </button>
              </div>
            </div>

            {/* History header */}
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2 text-fg-muted text-xs font-semibold uppercase tracking-wide">
                <History className="w-3.5 h-3.5" />
                History
                {jobsTotal > 0 && (
                  <span className="bg-fg/10 text-fg-muted px-1.5 py-0.5 rounded-full text-[10px]">
                    {jobsTotal}
                  </span>
                )}
              </div>
              <button onClick={loadJobs} className="text-fg-muted hover:text-fg transition-colors">
                <RotateCcw className={`w-3.5 h-3.5 ${loadingJobs ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Job list */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1" style={{ maxHeight: '55vh' }}>
              {loadingJobs && jobs.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-fg-muted text-sm gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />Loading history…
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-10 text-fg-muted">
                  <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No jobs yet.</p>
                  <p className="text-xs mt-1">Submit your first job above.</p>
                </div>
              ) : (
                jobs.map(job => (
                  <JobCard
                    key={job.job_id}
                    job={job}
                    isActive={activeJob?.job_id === job.job_id}
                    onSelect={setActiveJob}
                  />
                ))
              )}
            </div>
          </div>

          {/* RIGHT — active job detail */}
          <div className="flex-1 rounded-2xl border border-panel-border bg-panel p-5 overflow-hidden">
            <ActiveJobPanel
              job={activeJob}
              onClose={() => setActiveJob(null)}
            />
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TAB 2 — SETTINGS
      ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'settings' && (
        <div className="max-w-xl space-y-4">

          {/* Readiness checklist */}
          <div className="rounded-2xl border border-panel-border bg-panel p-4 mb-2">
            <p className="text-xs font-semibold text-fg-muted uppercase tracking-wide mb-3">Setup checklist</p>
            <div className="space-y-2">
              {[
                { done: !!s.api_key,              label: 'Gateway API key available'        },
                { done: !!model,                   label: 'Model selected'                  },
              ].map(({ done, label }) => (
                <div key={label} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className={`w-4 h-4 shrink-0 ${done ? 'text-green-400' : 'text-fg/20'}`} />
                  <span className={done ? 'text-fg' : 'text-fg-muted'}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Gateway API key */}
          <div className="rounded-2xl border border-panel-border bg-panel p-5">
            <div className="flex items-center gap-2 mb-3 text-fg font-semibold text-sm">
              <KeyRound className="w-4 h-4 text-fg-muted" />Gateway API key
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-fg/5 rounded-lg px-3 py-2 text-fg truncate font-mono">
                {s.api_key || '—'}
              </code>
              <button onClick={copyKey} title="Copy"
                className="px-3 py-2 rounded-lg border border-panel-border text-fg-muted hover:text-fg transition-colors">
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-fg-muted mt-2">Your tier, budget caps, and guardrails apply to all agent calls.</p>
          </div>

          <GitHubAppCard orgId={activeOrg?.org_id} notice={location.state?.githubNotice} />

          {/* GitHub token */}
          <div className="rounded-2xl border border-panel-border bg-panel p-5">
            <div className="flex items-center gap-2 mb-1 text-fg font-semibold text-sm">
              <Github className="w-4 h-4 text-fg-muted" />GitHub token
              {s.github_token_set && (
                <span className="ml-auto text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full border border-green-400/20 font-normal">
                  ••••{s.github_token_last4}
                </span>
              )}
            </div>
            <p className="text-xs text-fg-muted mb-3">
              {s.github_token_set
                ? 'Token is set and encrypted at rest. Enter a new one to replace it.'
                : 'Required to clone your private repos in the sandbox. Stored encrypted.'}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="password" value={token}
                onChange={e => setToken(e.target.value)}
                placeholder="ghp_…"
                autoComplete="off"
                className="flex-1 text-sm bg-surface border border-field-border rounded-lg px-3 py-2 text-fg
                  focus:border-orange outline-none"
              />
              <button onClick={loadRepos}
                disabled={loadingRepos || (!token.trim() && !s.github_token_set)}
                className="px-3 py-2 rounded-lg border border-panel-border text-fg-muted hover:text-fg text-sm
                  whitespace-nowrap transition-colors disabled:opacity-50 flex items-center gap-1.5">
                {loadingRepos
                  ? <><RefreshCw className="w-4 h-4 animate-spin" />Loading</>
                  : <><FolderGit2 className="w-4 h-4" />Load repos</>}
              </button>
            </div>
            {s.github_token_set && (
              <button onClick={clearToken}
                className="text-xs text-red-400 hover:text-red-300 mt-2 transition-colors">
                Remove token
              </button>
            )}
          </div>

          {/* Repo picker */}
          <div className="rounded-2xl border border-panel-border bg-panel p-5">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 text-fg font-semibold text-sm">
                <FolderGit2 className="w-4 h-4 text-fg-muted" />Allowed repositories
              </div>
              {selected.length > 0 && (
                <span className="text-xs bg-orange/10 text-orange border border-orange/20 px-2 py-0.5 rounded-full font-semibold">
                  {selected.length} selected
                </span>
              )}
            </div>
            {repoErr && <p className="text-xs text-red-400 mb-2">{repoErr}</p>}
            {repos.length === 0 ? (
              <p className="text-xs text-fg-muted py-2">
                {s.allowed_repos?.length
                  ? <>Currently: <span className="text-fg">{s.allowed_repos.join(', ')}</span>. Click "Load repos" to change.</>
                  : 'Set a token and click "Load repos" to pick which repos the agent can clone.'}
              </p>
            ) : (
              <div className="max-h-64 overflow-y-auto divide-y divide-panel-border/50 -mx-1 mt-2">
                {repos.map(r => (
                  <label key={r.full_name}
                    className="flex items-center gap-3 py-2.5 px-1 hover:bg-fg/5 rounded-lg cursor-pointer">
                    <input type="checkbox"
                      checked={selected.includes(r.full_name)}
                      onChange={() => toggleRepo(r.full_name)}
                      className="accent-orange w-4 h-4 shrink-0"
                    />
                    {r.private
                      ? <Lock className="w-3.5 h-3.5 text-fg-muted shrink-0" />
                      : <Globe className="w-3.5 h-3.5 text-fg-muted shrink-0" />}
                    <span className="text-sm text-fg truncate flex-1">{r.full_name}</span>
                    {r.language && (
                      <span className="text-[11px] text-fg-muted shrink-0">{r.language}</span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Model */}
          <div className="rounded-2xl border border-panel-border bg-panel p-5">
            <div className="flex items-center gap-2 mb-3 text-fg font-semibold text-sm">
              <Zap className="w-4 h-4 text-fg-muted" />Model
            </div>
            <select value={model} onChange={e => setModel(e.target.value)}
              className="w-full text-sm bg-surface border border-field-border rounded-lg px-3 py-2 text-fg
                focus:border-orange outline-none">
              {s.models.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <p className="text-xs text-fg-muted mt-2">Your tier governs which models are available.</p>
          </div>

          {/* Save */}
          <div className="flex items-center gap-3 pt-1">
            <button onClick={save} disabled={saving}
              className="px-5 py-2.5 rounded-lg bg-orange hover:bg-orange-hover text-white text-sm
                font-semibold transition-colors disabled:opacity-60">
              {saving ? 'Saving…' : 'Save settings'}
            </button>
            {saveMsg && (
              <span className={`text-sm ${saveMsg.includes('✓') ? 'text-green-400' : 'text-fg-muted'}`}>
                {saveMsg}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
